import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import axiosInstance from "../utils/axios.config"; // Change this import
import { message, Spin, Modal } from "antd"; // Add message, Spin, and Modal import from antd
import AddClientModal from "../components/clients/AddClientModal";
import EditClientModal from "../components/clients/EditClientModal";
import ViewClientModal from "../components/clients/ViewClientModal";
import Swal from "sweetalert2"; // Make sure Swal is imported at the top of your file
import PaymentReceipt from "../components/payments/PaymentReceipt";
import PaymentDetailsModal from "../components/payments/PaymentDetails";
import AddPaymentModal from "../components/payments/addPayment";
import PaymentsTable from "../components/payments/PaymentsTable";
import PermissionGuard from "../components/PermissionGuard";
import { PERMISSIONS } from "../config/permissionsConfig";
import { usePermissions } from "../context/PermissionsContext";
import logger from "../utils/logger";

const Clients = () => {
  // Permissions hook
  const { hasPermission } = usePermissions();
  
  // State initialization
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total_clients: 0,
    plaintiffs: 0,  // مدعون
    defendants: 0,  // مدعى عليهم
    with_lawyers: 0,
    with_phones: 0,
    with_emails: 0,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("الكل");
  const [sortBy, setSortBy] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false); // Add this line back
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Fetch clients from API
  useEffect(() => {
    fetchClients();
    fetchStats();
  }, []);

  // تعديل التعامل مع البيانات المستلمة
  const fetchClients = async () => {
    try {
      setLoading(true);
      setError(null);

      const token =
        localStorage.getItem("token") || sessionStorage.getItem("token");
      if (!token) {
        setError("جلسة المستخدم منتهية. يرجى تسجيل الدخول مرة أخرى.");
        return;
      }

      const response = await axiosInstance.get("/clients");

      if (response.data && Array.isArray(response.data.data)) {
        // تحويل البيانات وإضافة خصائص إضافية مفيدة
        const formattedClients = response.data.data.map((client) => ({
          ...client,
          key: client.id, // إضافة مفتاح فريد لكل عميل
          type: client.client_type || "غير محدد",
          cases_count: client.cases_count || 0,
          status: client.status || "نشط",
          // إضافة تنسيق للتواريخ إذا كانت موجودة
          power_of_attorney_date: client.power_of_attorney_date
            ? new Date(client.power_of_attorney_date)
                .toISOString()
                .split("T")[0]
            : null,
        }));
        setClients(formattedClients);
        if (formattedClients.length === 0) {
          message.info("لا يوجد عملاء في النظام حالياً");
        }
      } else {
        throw new Error("تنسيق البيانات غير صحيح");
      }
    } catch (err) {
      logger.error("Error fetching clients:", err);
      setError(
        "حدث خطأ في جلب بيانات العملاء: " +
          (err.response?.data?.message || err.message)
      );
      message.error("فشل في جلب بيانات العملاء");
    } finally {
      setLoading(false);
    }
  };

  // Fetch clients stats from API
  const fetchStats = async () => {
    try {
      // جلب بيانات العملاء مباشرة بدلاً من الإحصائيات
      const clientsResponse = await axiosInstance.get("/clients");

      if (clientsResponse.data && clientsResponse.data.data) {
        const clientsData = clientsResponse.data.data;

        // حساب عدد المدعين والمدعى عليهم من بيانات العملاء
        let plaintiffs = 0;  // مدعون
        let defendants = 0;  // مدعى عليهم
        let with_lawyers = 0;  // الذين لديهم وكالات
        let active_cases = 0;  // الذين لديهم قضايا نشطة
        let pending_payments = 0;  // الذين عليهم دفعات مستحقة

        clientsData.forEach(client => {
          if (client.client_type === "مدعي") {
            plaintiffs++;
          } else if (client.client_type === "مدعى عليه") {
            defendants++;
          }

          if (client.power_of_attorney_number) with_lawyers++;
          if (client.cases_count > 0) active_cases++;
          if (client.pending_payments) pending_payments++;
        });

        const statsData = {
          total_clients: clientsData.length,
          plaintiffs: plaintiffs,
          defendants: defendants,
          with_lawyers: with_lawyers,
          with_phones: active_cases,
          with_emails: pending_payments,
        };

        setStats(statsData);
      } else {
        logger.error("Invalid clients response format:", clientsResponse.data);
        throw new Error("تنسيق البيانات غير صحيح");
      }
    } catch (error) {
      logger.error("Error fetching stats:", error);
      setError(
        "حدث خطأ في جلب بيانات الإحصائيات: " +
          (error.response?.data?.message || error.message)
      );
      message.error("فشل في جلب بيانات الإحصائيات");
    }
  };

  // Modified handlers to work with API
  const handleDeleteClient = async (id) => {
    try {
      const clientToDelete = clients.find((c) => c.id === id);

      // SweetAlert2 confirmation dialog
      const result = await Swal.fire({
        title: "تأكيد الحذف",
        html: `
                هل أنت متأكد من حذف العميل التالي؟<br>
                <b>الاسم:</b> ${clientToDelete.name}<br>
                <b>رقم الهاتف:</b> ${clientToDelete.phone || "غير محدد"}<br>
                <b>عدد القضايا:</b> ${clientToDelete.cases_count || 0}
            `,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "نعم، حذف",
        cancelButtonText: "إلغاء",
        reverseButtons: true,
      });

      if (!result.isConfirmed) {
        return; // تم إلغاء الحذف
      }

      // إظهار مؤشر التحميل
      Swal.fire({
        title: "جاري الحذف...",
        text: "يرجى الانتظار",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await axiosInstance.delete(`/clients/${id}`);

      if (response.status === 200) {
        setClients(clients.filter((client) => client.id !== id));

        Swal.fire({
          title: "تم الحذف!",
          text: "تم حذف العميل بنجاح",
          icon: "success",
          timer: 1500,
        });
      } else {
        throw new Error("فشل حذف العميل");
      }
    } catch (error) {
      logger.error("Client deletion error:", error);

      // حالة خاصة للعملاء المرتبطين بقضايا
      if (error.response?.status === 409) {
        Swal.fire({
          title: "لا يمكن الحذف!",
          text: "لا يمكن حذف العميل لوجود قضايا مرتبطة به",
          icon: "warning",
        });
      } else {
        Swal.fire({
          title: "خطأ!",
          text:
            error.response?.data?.message ||
            error.message ||
            "فشل في حذف العميل",
          icon: "error",
        });
      }
    }
  };

  const handleViewClient = (client) => {
    setSelectedClient(client);
    setShowViewModal(true);
  };

  // تعديل هذه الوظيفة لتكون فارغة مؤقتاً
  const handleEditClient = (client) => {
    setSelectedClient(client);
    setShowEditModal(true);
  };

  const handleEditSuccess = () => {
    setShowEditModal(false);
    setSelectedClient(null);
    fetchClients(); // إعادة تحميل قائمة العملاء
    fetchStats(); // إعادة تحميل الإحصائيات
  };

  // تحسين وظيفة البحث والفلترة والفرز
  const filteredClients = clients.filter((client) => {
    // تحويل نص البحث إلى حروف صغيرة وإزالة المسافات الزائدة
    const search = searchTerm.toLowerCase().trim();

    // التأكد من وجود القيم قبل البحث فيها
    const nameMatch = client.name?.toLowerCase().includes(search);
    const phoneMatch = client.phone?.toLowerCase().includes(search);
    const emailMatch = client.email?.toLowerCase().includes(search);
    const nationalIdMatch = client.national_id?.toLowerCase().includes(search);
    const attorneyNumberMatch = client.power_of_attorney_number
      ?.toLowerCase()
      .includes(search);

    // البحث في كل الحقول
    const matchesSearch =
      search === "" || // إذا كان حقل البحث فارغاً، اعرض كل النتائج
      nameMatch ||
      phoneMatch ||
      emailMatch ||
      nationalIdMatch ||
      attorneyNumberMatch;

    // فلترة حسب نوع العميل
    const matchesType =
      filterType === "الكل" || client.client_type === filterType;

    return matchesSearch && matchesType;
  }).sort((a, b) => {
    // تطبيق الفرز حسب الاختيار
    if (!sortBy) return 0;
    
    switch(sortBy) {
      case "newest":
        // ترتيب حسب الأحدث (تاريخ الإنشاء الأحدث)
        return new Date(b.created_at) - new Date(a.created_at);
      case "oldest":
        // ترتيب حسب الأقدم (تاريخ الإنشاء الأقدم)
        return new Date(a.created_at) - new Date(b.created_at);
      case "name-asc":
        // ترتيب حسب الاسم (من الألف إلى الياء)
        return a.name.localeCompare(b.name);
      case "name-desc":
        // ترتيب حسب الاسم (من الياء إلى الألف)
        return b.name.localeCompare(a.name);
      default:
        return 0;
    }
  });

  // حساب الـ pagination
  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);
  const currentClients = filteredClients.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const getPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
    return pages;
  };

  // تحديث دالة تنسيق التاريخ
  const formatDate = (dateString) => {
    if (!dateString) return "لا يوجد";
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      // console.error('خطأ في تنسيق التاريخ:', error);
      return "تاريخ غير صالح";
    }
  };

  // تعديل مكون جدول العملاء
  const ClientsTable = () => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-2 md:px-6 py-2 md:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              العميل
            </th>
            <th className="px-2 md:px-6 py-2 md:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
              معلومات التواصل
            </th>
            <th className="px-2 md:px-6 py-2 md:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
              معلومات الوكالة
            </th>
            <th className="px-2 md:px-6 py-2 md:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              نوع العميل
            </th>
            <th className="px-2 md:px-6 py-2 md:py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
              القضايا
            </th>
            <th className="px-2 md:px-6 py-2 md:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              إجراءات
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {currentClients.map((client) => (
            <tr key={client.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleViewClient(client)}>
              <td className="px-2 md:px-6 py-2 md:py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">
                  {client.name}
                </div>
                <div className="text-xs text-gray-500 md:hidden">
                  {client.phone ? (
                    <a
                      href={`https://wa.me/${client.phone.replace(/[^0-9]/g, '').startsWith('964') ? client.phone.replace(/[^0-9]/g, '') : '964' + client.phone.replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600 hover:underline flex items-center gap-1"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" className="bi bi-whatsapp" viewBox="0 0 16 16">
                        <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.557 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007-.133 0-.347.05-.529.248-.182.198-.691.677-.691 1.654 0 .977.71 1.916.81 2.049.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z"/>
                      </svg>
                      {client.phone}
                    </a>
                  ) : (
                    "لا يوجد رقم هاتف"
                  )}
                </div>
              </td>
              <td className="px-2 md:px-6 py-2 md:py-4 hidden md:table-cell">
                <div className="text-sm text-gray-500">
                  <div>
                    {client.phone ? (
                      <a
                        href={`https://wa.me/${client.phone.replace(/[^0-9]/g, '').startsWith('964') ? client.phone.replace(/[^0-9]/g, '') : '964' + client.phone.replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-600 hover:underline flex items-center gap-1"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" className="bi bi-whatsapp" viewBox="0 0 16 16">
                          <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.557 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007-.133 0-.347.05-.529.248-.182.198-.691.677-.691 1.654 0 .977.71 1.916.81 2.049.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z"/>
                        </svg>
                        {client.phone}
                      </a>
                    ) : (
                      "لا يوجد رقم هاتف"
                    )}
                  </div>
                  <div className="text-xs">
                    {client.email || "لا يوجد بريد إلكتروني"}
                  </div>
                  <div className="text-xs truncate max-w-xs">
                    {client.address || "لا يوجد عنوان"}
                  </div>
                </div>
              </td>
              <td className="px-2 md:px-6 py-2 md:py-4 hidden md:table-cell">
                <div className="text-sm text-gray-500">
                  <div className="font-medium">
                    رقم الوكالة: {client.power_of_attorney_number || "لا يوجد"}
                  </div>
                  <div className="text-xs">
                    التاريخ: {formatDate(client.power_of_attorney_date)}
                  </div>
                </div>
              </td>
              <td className="px-2 md:px-6 py-2 md:py-4 whitespace-nowrap">
                <span
                  className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
                ${
                  client.client_type === "مدعي"
                    ? "bg-green-100 text-green-800"
                    : "bg-yellow-100 text-yellow-800"
                }`}
                >
                  {client.client_type || "غير محدد"}
                </span>
              </td>
              <td className="px-2 md:px-6 py-2 md:py-4 whitespace-nowrap text-center hidden sm:table-cell">
                <span className="px-2 py-1 text-sm font-medium text-gray-700">
                  {client.cases_count || 0}
                </span>
              </td>
              <td className="px-2 md:px-6 py-2 md:py-4 whitespace-nowrap text-sm font-medium text-center">
                <div className="flex justify-center gap-1 md:gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewClient(client);
                    }}
                    className="text-blue-600 hover:text-blue-900 p-1"
                    aria-label="عرض"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  </button>
                  {(hasPermission(PERMISSIONS.CLIENTS_EDIT_ALL) || 
                    (hasPermission(PERMISSIONS.CLIENTS_EDIT_OWN) && client.created_by === JSON.parse(localStorage.getItem('user') || '{}').id)) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditClient(client);
                      }}
                      className="text-green-600 hover:text-green-900 p-1"
                      aria-label="تعديل"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                  )}
                  {hasPermission(PERMISSIONS.CLIENTS_DELETE) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClient(client.id);
                      }}
                      className="text-red-600 hover:text-red-900 p-1"
                      aria-label="حذف"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const handleAddSuccess = () => {
    setShowAddModal(false);
    fetchClients(); // إعادة تحميل قائمة العملاء
    fetchStats(); // إعادة تحميل الإحصائيات
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-100 p-3 md:p-8" dir="rtl">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 md:mb-6 gap-3">
            <h1 className="text-xl md:text-2xl font-bold text-gray-800">
              إدارة العملاء
            </h1>
            <PermissionGuard permission={PERMISSIONS.CLIENTS_CREATE}>
              <button
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 w-full md:w-auto"
                onClick={() => setShowAddModal(true)}
              >
                إضافة عميل جديد
              </button>
            </PermissionGuard>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    إجمالي العملاء
                  </dt>
                  <dd className="mt-1 text-3xl font-semibold text-gray-900">
                    {stats.total_clients}
                  </dd>
                </dl>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    مدعون
                  </dt>
                  <dd className="mt-1 text-3xl font-semibold text-gray-900">
                    {stats.plaintiffs}
                  </dd>
                </dl>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    مدعى عليهم
                  </dt>
                  <dd className="mt-1 text-3xl font-semibold text-gray-900">
                    {stats.defendants}
                  </dd>
                </dl>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    لديهم وكالات
                  </dt>
                  <dd className="mt-1 text-3xl font-semibold text-gray-900">
                    {stats.with_lawyers}
                  </dd>
                </dl>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    لديهم قضايا نشطة
                  </dt>
                  <dd className="mt-1 text-3xl font-semibold text-gray-900">
                    {stats.with_phones}
                  </dd>
                </dl>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    عليهم دفعات مستحقة
                  </dt>
                  <dd className="mt-1 text-3xl font-semibold text-gray-900">
                    {stats.with_emails}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          {/* Search & Filters */}
          <div className="bg-white p-3 md:p-4 rounded-lg shadow-sm mb-4 md:mb-6">
            <div className="flex flex-col md:flex-row gap-3 md:items-end">
              {/* Search Input */}
              <div className="w-full md:flex-1 mb-2 md:mb-0">
                <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">البحث عن عميل</label>
                <input
                  type="text"
                  id="search"
                  placeholder="اكتب اسم العميل أو رقم الهوية..."
                  className="w-full p-2 md:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              {/* Filter Select */}
              <div className="w-full md:w-48">
                <label htmlFor="filter" className="block text-sm font-medium text-gray-700 mb-1">نوع العميل</label>
                <select
                  id="filter"
                  className="w-full p-2 md:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="الكل">جميع العملاء</option>
                  <option value="مدعي">مدعي</option>
                  <option value="مدعى عليه">مدعى عليه</option>
                </select>
              </div>
              
              {/* Sort Select */}
              <div className="w-full md:w-48">
                <label htmlFor="sort" className="block text-sm font-medium text-gray-700 mb-1">الترتيب حسب</label>
                <select
                  id="sort"
                  className="w-full p-2 md:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="">الأحدث أولاً</option>
                  <option value="newest">الأحدث أولاً</option>
                  <option value="oldest">الأقدم أولاً</option>
                  <option value="name-asc">حسب الاسم (أ-ي)</option>
                  <option value="name-desc">حسب الاسم (ي-أ)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Clients Table */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {loading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                <p className="mt-2">جاري التحميل...</p>
              </div>
            ) : error ? (
              <div className="text-center py-4 text-red-600 px-3">
                <p>{error}</p>
                <button
                  onClick={fetchClients}
                  className="mt-2 text-blue-600 hover:text-blue-800"
                >
                  إعادة المحاولة
                </button>
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                لا يوجد عملاء للعرض
              </div>
            ) : (
              <ClientsTable />
            )}
          </div>

          {/* Pagination */}
          {filteredClients.length > 0 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  السابق
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="mr-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  التالي
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    عرض{" "}
                    <span className="font-medium">
                      {(currentPage - 1) * itemsPerPage + 1}
                    </span>
                    {" "}- {" "}
                    <span className="font-medium">
                      {Math.min(currentPage * itemsPerPage, filteredClients.length)}
                    </span>
                    {" "}من{" "}
                    <span className="font-medium">{filteredClients.length}</span>
                    {" "}عميل
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">السابق</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {getPageNumbers().map((number) => (
                      <button
                        key={number}
                        onClick={() => handlePageChange(number)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === number
                            ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                        }`}
                      >
                        {number}
                      </button>
                    ))}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">التالي</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}

          {/* Client Details Modal */}
          <ViewClientModal
            visible={showViewModal}
            client={selectedClient}
            onClose={() => {
              setShowViewModal(false);
              setSelectedClient(null);
            }}
          />

          {/* Add Client Modal */}
          <AddClientModal
            visible={showAddModal}
            onClose={() => setShowAddModal(false)}
            onSuccess={handleAddSuccess}
          />

          {/* Edit Client Modal */}
          <EditClientModal
            visible={showEditModal}
            onClose={() => setShowEditModal(false)}
            onSuccess={handleEditSuccess}
            client={selectedClient}
          />
        </div>
      </div>
    </Layout>
  );
};

export default Clients;
